import React, { useState, useEffect } from "react";
import { Radio, Copy, Eye, EyeOff, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Fetches RTMPS ingest credentials for an active Cloudflare Stream live input
 * and walks the creator through OBS/Streamlabs setup in 3 steps.
 *
 * Once credentials are loaded, polls Cloudflare every 5s for the encoder
 * connection state — so the creator sees a live "OBS connected ✓" badge
 * the moment their broadcaster sends its first frame.
 */
export default function GoLiveOBSPanel({ streamId }) {
  const [creds, setCreds] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reveal, setReveal] = useState(false);
  // Cloudflare encoder status — null = not yet polled
  const [cfStatus, setCfStatus] = useState(null);

  const load = async () => {
    if (!streamId) return toast.error("Start a live stream first");
    setLoading(true);
    try {
      const { data } = await api.get(`/live/${streamId}/broadcast-credentials`);
      setCreds(data);
      if (!data.configured) toast.info("Real streaming not configured — mock creds shown.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Poll the live-input status every 5s while creds are visible + configured.
  // We don't show the "Connected" badge until the first poll returns to avoid
  // flashing "Disconnected" on a freshly-created input.
  useEffect(() => {
    if (!creds?.configured || !streamId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/live/${streamId}/cf-status`);
        if (!cancelled) setCfStatus(data);
      } catch { /* keep last status */ }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [streamId, creds?.configured]);

  const copy = async (val, label) => {
    try {
      await navigator.clipboard.writeText(val);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy blocked — long-press to copy");
    }
  };

  return (
    <section data-testid="obs-panel" className="tv-glass rounded-2xl border border-white/10 p-6 max-w-3xl">
      <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold flex items-center gap-2">
        <Radio className="w-4 h-4" /> Go live with OBS / Streamlabs
      </div>
      <h3 className="font-display font-black text-2xl text-white tracking-tight mt-1">Broadcast from desktop</h3>
      <p className="text-white/60 text-sm mt-2">
        Use any RTMPS broadcaster (OBS, Streamlabs, vMix, ecamm) to go live with full camera, scene switching, and overlays.
      </p>

      <button
        data-testid="obs-load-creds-btn"
        onClick={load}
        disabled={loading}
        className="mt-4 bg-tv-accent hover:bg-yellow-400 disabled:opacity-50 text-black font-bold px-5 py-2 rounded-full text-sm"
      >
        {loading ? "Fetching…" : creds ? "Refresh credentials" : "Show broadcast credentials"}
      </button>

      {creds && (
        <div className="mt-6 space-y-4" data-testid="obs-creds">
          {!creds.configured && (
            <div className="flex gap-2 items-start text-xs text-yellow-300 bg-yellow-400/10 border border-yellow-400/20 rounded-lg px-3 py-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <b>Cloudflare Stream isn't connected yet.</b> Ask an admin to open <a href="/admin" className="underline text-white hover:text-tv-accent">Admin → Integrations → Cloudflare Stream</a> and paste the Account ID + API Token. Once saved, return here and press <em>Refresh credentials</em> — no restart needed.
                <div className="mt-1 text-white/70">{creds.message}</div>
              </div>
            </div>
          )}

          {creds.configured && (
            <>
              {/* Live encoder connection badge — flips to green the moment
                  the creator's OBS / Streamlabs starts pushing frames so
                  they don't have to guess whether their setup is working. */}
              <div
                data-testid="obs-cf-status"
                className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 border ${
                  cfStatus?.connected
                    ? "text-emerald-200 bg-emerald-500/10 border-emerald-500/30"
                    : "text-yellow-200 bg-yellow-400/10 border-yellow-400/30"
                }`}
              >
                {cfStatus?.connected ? (
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                ) : (
                  <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
                )}
                <div className="leading-tight">
                  <div className="font-bold">
                    {cfStatus?.connected ? "Encoder connected — you're broadcasting" : "Waiting for OBS / Streamlabs to connect…"}
                  </div>
                  <div className="text-[11px] opacity-75">{cfStatus?.message || "Polling Cloudflare every 5s."}</div>
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold mb-1">RTMPS server URL</div>
                <div className="flex gap-2">
                  <code data-testid="obs-rtmps-url" className="flex-1 text-white text-sm bg-white/5 border border-white/10 rounded px-3 py-2 font-mono select-all break-all">{creds.rtmps_url}</code>
                  <button onClick={() => copy(creds.rtmps_url, "RTMPS URL")} className="bg-white/10 hover:bg-white/20 text-white px-3 rounded" aria-label="Copy RTMPS URL">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold mb-1 flex items-center gap-2">
                  Stream key
                  <button onClick={() => setReveal((r) => !r)} className="text-white/60 hover:text-white" aria-label="Toggle key visibility">
                    {reveal ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
                <div className="flex gap-2">
                  <code data-testid="obs-stream-key" className="flex-1 text-white text-sm bg-white/5 border border-white/10 rounded px-3 py-2 font-mono select-all break-all">
                    {reveal ? creds.rtmps_stream_key : "•".repeat(Math.min(40, (creds.rtmps_stream_key || "").length))}
                  </code>
                  <button onClick={() => copy(creds.rtmps_stream_key, "Stream key")} className="bg-white/10 hover:bg-white/20 text-white px-3 rounded" aria-label="Copy stream key">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-[11px] text-white/40 mt-1">Never share this key publicly — anyone with it can broadcast as you.</div>
              </div>
            </>
          )}

          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold mb-2">3-step OBS setup</div>
            <ol className="space-y-2 text-sm text-white/85 list-decimal list-inside" data-testid="obs-steps">
              <li>Open OBS → <b className="text-white">Settings → Stream</b>.</li>
              <li>Set <b className="text-white">Service</b> to <i>Custom</i>. Paste the RTMPS URL as <b>Server</b> and the Stream Key above.</li>
              <li>Click <b className="text-white">Apply</b>, then <b className="text-white">Start Streaming</b>. You'll appear live on Tap IN TV in ~5 seconds.</li>
            </ol>
            <div className="flex gap-2 items-center text-xs text-white/60 mt-3">
              <CheckCircle2 className="w-4 h-4 text-tv-accent" /> Auto VOD archive to your Library when you stop the stream.
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
